import { MultiSelect, Button, DatePicker } from '@atrvd/saas-ui'
import { Plus, X } from 'lucide-react'
import { toLocalDateString, safeParseDate } from '@/lib/date-utils'
import type { ProjectionFilters } from '@/types/projections'

interface ProjectionFiltersProps {
  filters: ProjectionFilters
  onFiltersChange: (filters: ProjectionFilters) => void
  onAddProjection: () => void
  onClearFilters?: () => void
  facilityOptions?: Array<{ value: string; label: string }>
  categoryOptions?: Array<{ value: string; label: string }>
}

/**
 * Projection filters component matching the UI mockup design
 * Facility, Category, Projected payment date, Created date/time + Add Projection button
 */
export function ProjectionFilters({
  filters,
  onFiltersChange,
  onAddProjection,
  onClearFilters,
  facilityOptions = [],
  categoryOptions = [],
}: ProjectionFiltersProps) {
  // Check if any filters are active
  const hasActiveFilters = !!(
    filters.facilityIds?.length ||
    filters.categoryIds?.length ||
    filters.startDate ||
    filters.createdDate
  )

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* Facility Filter */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">Facility:</span>
          <MultiSelect
            options={facilityOptions}
            value={filters.facilityIds || []}
            onChange={(value) => onFiltersChange({ ...filters, facilityIds: value.length > 0 ? value : undefined })}
            placeholder="All"
            className="w-[200px]"
            searchable
            searchPlaceholder="Search facilities..."
          />
        </div>

        {/* Category Filter */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">Category:</span>
          <MultiSelect
            options={categoryOptions}
            value={filters.categoryIds || []}
            onChange={(value) => onFiltersChange({ ...filters, categoryIds: value.length > 0 ? value : undefined })}
            placeholder="All"
            className="w-[200px]"
            searchable
            searchPlaceholder="Search categories..."
          />
        </div>

        {/* Projected Payment Date Filter */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">Projected payment date:</span>
          <DatePicker
            date={safeParseDate(filters.startDate)}
            onDateChange={(date) => {
              // Convert at the UI edge, right where the DatePicker hands back a
              // LOCAL-midnight Date — the data layer then only ever sees a plain
              // YYYY-MM-DD string with no timezone to misinterpret (DCP-386).
              // Single-day range: the same calendar day drives both bounds.
              const dateStr = date ? toLocalDateString(date) : undefined
              onFiltersChange({
                ...filters,
                startDate: dateStr,
                endDate: dateStr,
              })
            }}
            placeholder="Select date"
            className="w-[200px]"
          />
        </div>

        {/* Created Filter */}
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">Created:</span>
          <DatePicker
            date={safeParseDate(filters.createdDate)}
            onDateChange={(date) => {
              onFiltersChange({
                ...filters,
                createdDate: date ? toLocalDateString(date) : undefined,
              })
            }}
            placeholder="Select date"
            className="w-[200px]"
          />
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && onClearFilters && (
          <Button
            onClick={onClearFilters}
            variant="ghost"
            size="icon"
            className="self-end h-8 w-8"
            title="Clear filters"
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        {/* Add Projection Button (Red) — always pinned to the right */}
        <Button
          onClick={onAddProjection}
          className="bg-red-600 hover:bg-red-700 text-white self-end ml-auto"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add projection
        </Button>
      </div>
    </div>
  )
}
